import * as CookieConsent from "vanilla-cookieconsent";
import { create } from "zustand";

// Web-only: Google Analytics runs in the customer app, not admin.
import { disableAnalytics, enableAnalytics } from "./google-analytics";
import { PRIVACY_URL } from "./marketing-url";

/**
 * Cookie consent for app.hrdocket.com.
 *
 * `vanilla-cookieconsent` is used purely as the **consent engine** — it owns the
 * category model, the signed `cc_cookie`, revision invalidation, expiry and the
 * accept/reject callbacks. Its own banner and preferences modal are never shown:
 * we boot it with `autoShow: false` and never call `showPreferences()`, so every
 * pixel comes from our shadcn components in `@/components/cookie-consent`. That
 * keeps the plugin's (well-tested) storage semantics without inheriting a second
 * design system, and means the library's stylesheet is deliberately *not*
 * imported anywhere.
 *
 * `autoShow: false` only suppresses *showing* those modals — `run()` still builds
 * their markup into a `#cc-main` container on the body when there's no valid
 * consent yet. Unstyled, that leaks onto the page as raw text, so `#cc-main` is
 * hidden in `globals.css`; keep that rule as long as this file exists.
 *
 * The engine is mounted once, app-wide, from the root layout — consent has to be
 * obtainable and revocable on the sign-in screen, the dashboard and the portal
 * alike, not just on the front door.
 */

/** Bump when the categories or their wording change materially — the plugin
 * treats a mismatched revision as "no consent" and re-prompts everyone. */
const CONSENT_REVISION = 1;

export type CookieCategoryId = "necessary" | "analytics" | "marketing";

export type CookieCategory = {
  id: CookieCategoryId;
  label: string;
  description: string;
  /** Necessary cookies can't be switched off, so their toggle renders disabled. */
  locked: boolean;
};

/**
 * The categories shown in our own preferences dialog. Kept here (rather than in
 * the plugin's `language.translations`) because our UI reads them directly and
 * the plugin's copy is never displayed.
 */
export const COOKIE_CATEGORIES: readonly CookieCategory[] = [
  {
    id: "necessary",
    label: "Strictly necessary",
    description:
      "Required for HR Docket to work — signing you in, keeping your session and workspace active, remembering your theme, and protecting forms from abuse. These can't be turned off.",
    locked: true,
  },
  {
    id: "analytics",
    label: "Analytics",
    description:
      "Help us understand which features are used and where people get stuck, so we can improve the product. Aggregated and never used to identify you.",
    locked: false,
  },
  {
    id: "marketing",
    label: "Marketing",
    description:
      "Let us measure which campaigns bring people to HR Docket and show relevant messaging. Off unless you turn it on.",
    locked: false,
  },
] as const;

const OPTIONAL_CATEGORIES = COOKIE_CATEGORIES.filter((c) => !c.locked).map((c) => c.id);

type AcceptedMap = Record<CookieCategoryId, boolean>;

const NONE_ACCEPTED: AcceptedMap = {
  necessary: true,
  analytics: false,
  marketing: false,
};

type CookieConsentState = {
  /** True once `CookieConsent.run()` has resolved — UI stays hidden until then. */
  ready: boolean;
  /** No valid consent on record, so the banner should be showing. */
  needsConsent: boolean;
  /** Our shadcn preferences dialog. */
  preferencesOpen: boolean;
  /** Live per-category consent, mirrored from the plugin. */
  accepted: AcceptedMap;
  setReady: (needsConsent: boolean, accepted: AcceptedMap) => void;
  setAccepted: (accepted: AcceptedMap) => void;
  setNeedsConsent: (needsConsent: boolean) => void;
  setPreferencesOpen: (open: boolean) => void;
};

const useCookieConsentStore = create<CookieConsentState>((set) => ({
  ready: false,
  needsConsent: false,
  preferencesOpen: false,
  accepted: NONE_ACCEPTED,
  setReady: (needsConsent, accepted) => set({ ready: true, needsConsent, accepted }),
  setAccepted: (accepted) => set({ accepted }),
  setNeedsConsent: (needsConsent) => set({ needsConsent }),
  setPreferencesOpen: (preferencesOpen) => set({ preferencesOpen }),
}));

/** Subscribe a component to consent state (banner, dialog, settings row). */
export function useCookieConsent() {
  return useCookieConsentStore();
}

/** Open the preferences dialog from anywhere — the auth footer, settings, a link. */
export function openCookiePreferences(): void {
  useCookieConsentStore.getState().setPreferencesOpen(true);
}

/** Read the plugin's current state into the shape our components render from. */
function readAccepted(): AcceptedMap {
  return {
    necessary: true,
    analytics: CookieConsent.acceptedCategory("analytics"),
    marketing: CookieConsent.acceptedCategory("marketing"),
  };
}

/**
 * The seam where consent actually switches things on. Sentry is not gated — it
 * is loaded as a strictly necessary service (error reporting on the login screen
 * has to survive a rejection). Google Analytics is: it loads only once analytics
 * is accepted, and is disabled with its cookies erased when that is withdrawn.
 * `onConsent` fires on every page load and `onChange` on every later edit, so
 * both paths run.
 */
function applyConsent(accepted: AcceptedMap): void {
  if (accepted.analytics) {
    void enableAnalytics();
  } else {
    void disableAnalytics();
    CookieConsent.eraseCookies(/^_ga/, "/", window.location.hostname);
  }

  if (accepted.marketing) {
    // e.g. load marketing/attribution pixels here.
  }
}

let booted = false;

/**
 * Boot the consent engine. Safe to call more than once (React strict mode
 * double-invokes effects); only the first call reaches the plugin.
 */
export async function initCookieConsent(): Promise<void> {
  if (booted) return;
  booted = true;

  const store = useCookieConsentStore.getState();

  await CookieConsent.run({
    // Our components are the entire UI — never let the plugin paint.
    autoShow: false,
    mode: "opt-in",
    revision: CONSENT_REVISION,
    // We don't use `type="text/plain" data-category` script tags; consent is
    // applied imperatively in `applyConsent`.
    manageScriptTags: false,
    cookie: {
      name: "hrd_cookie_consent",
      expiresAfterDays: 182,
      sameSite: "Lax",
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {},
      marketing: {},
    },
    /**
     * Required by the plugin even though its modals never render. Kept
     * deliberately terse: the real, user-facing copy lives in our components.
     */
    language: {
      default: "en",
      translations: {
        en: {
          consentModal: {
            title: "We use cookies",
            description: "HR Docket uses cookies to run the app and improve it.",
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            showPreferencesBtn: "Manage preferences",
            footer: `<a href="${PRIVACY_URL}" target="_blank" rel="noreferrer">Privacy Policy</a>`,
          },
          preferencesModal: {
            title: "Cookie preferences",
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            savePreferencesBtn: "Save preferences",
            closeIconLabel: "Close",
            sections: COOKIE_CATEGORIES.map((category) => ({
              title: category.label,
              description: category.description,
              linkedCategory: category.id,
            })),
          },
        },
      },
    },
    onConsent: () => {
      const accepted = readAccepted();
      store.setAccepted(accepted);
      store.setNeedsConsent(false);
      applyConsent(accepted);
    },
    onChange: () => {
      const accepted = readAccepted();
      store.setAccepted(accepted);
      applyConsent(accepted);
    },
  });

  // `onConsent` only fires for users who already decided; first-time visitors
  // land here with no valid consent and get the banner.
  const hasConsent = CookieConsent.validConsent();
  store.setReady(!hasConsent, hasConsent ? readAccepted() : NONE_ACCEPTED);
}

/** Accept every category. */
export function acceptAllCookies(): void {
  CookieConsent.acceptCategory("all");
  useCookieConsentStore.getState().setNeedsConsent(false);
}

/** Reject everything optional — necessary stays on because it's `readOnly`. */
export function rejectAllCookies(): void {
  CookieConsent.acceptCategory([]);
  useCookieConsentStore.getState().setNeedsConsent(false);
}

/** Persist an exact selection from the preferences dialog. */
export function saveCookieSelection(accepted: AcceptedMap): void {
  const chosen = OPTIONAL_CATEGORIES.filter((id) => accepted[id]);
  CookieConsent.acceptCategory(chosen);
  useCookieConsentStore.getState().setNeedsConsent(false);
}
